import React, { useState, useEffect } from 'react';
import { api, projectApi } from '../services/api';

const TeamManagement = () => {
  const [activeView, setActiveView] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  
  const [teamMembers, setTeamMembers] = useState([
    {
      id: 'TM-001',
      name: 'Mike Rodriguez',
      email: 'mike@example.com',
      type: '1099',
      role: 'Senior Drafter',
      status: 'active',
      rate: 45,
      rateType: 'hourly',
      projects: 3,
      hoursThisWeek: 32,
      earnings: { month: 5400, ytd: 48600 },
      rewardPoints: 450,
      joinDate: '2022-03-15',
      lastCheckin: '2024-01-08 08:15 AM'
    },
    {
      id: 'TM-002',
      name: 'Sarah Chen',
      email: 'sarah@example.com',
      type: 'W2',
      role: 'CAD Technician',
      status: 'active',
      rate: 52000,
      rateType: 'salary',
      projects: 2,
      hoursThisWeek: 40,
      earnings: { month: 4333, ytd: 39000 },
      rewardPoints: 680,
      joinDate: '2021-08-01',
      lastCheckin: '2024-01-08 08:30 AM',
      taxWithholding: { federal: 18, state: 4, fica: 7.65 }
    },
    {
      id: 'TM-003',
      name: 'James Wilson',
      email: 'james@example.com',
      type: '1099',
      role: 'Drafter',
      status: 'active',
      rate: 35,
      rateType: 'hourly',
      projects: 2,
      hoursThisWeek: 28,
      earnings: { month: 3920, ytd: 35280 },
      rewardPoints: 320,
      joinDate: '2023-01-10',
      lastCheckin: null
    }
  ]);

  const filteredMembers = activeView === 'all' 
    ? teamMembers 
    : teamMembers.filter(m => m.type === activeView);

  return (
    <div className="team-management">
      {/* Header */}
      <div className="section-header">
        <h2>👥 Team Management</h2>
        <button className="btn primary" onClick={() => setShowAddModal(true)}>
          + Add Team Member
        </button>
      </div>

      {/* Filter Tabs */}
      <div className="filter-tabs">
        <button 
          className={`filter-tab ${activeView === 'all' ? 'active' : ''}`}
          onClick={() => setActiveView('all')}
        >
          All ({teamMembers.length})
        </button>
        <button 
          className={`filter-tab ${activeView === '1099' ? 'active' : ''}`}
          onClick={() => setActiveView('1099')}
        >
          1099 Contractors ({teamMembers.filter(m => m.type === '1099').length})
        </button>
        <button 
          className={`filter-tab ${activeView === 'W2' ? 'active' : ''}`}
          onClick={() => setActiveView('W2')}
        >
          W2 Employees ({teamMembers.filter(m => m.type === 'W2').length})
        </button>
      </div>

      {/* Team Stats */}
      <div className="team-stats">
        <div className="stat-card">
          <span className="stat-icon">👥</span>
          <div className="stat-info">
            <span className="stat-value">{teamMembers.length}</span>
            <span className="stat-label">Total Team</span>
          </div>
        </div>
        <div className="stat-card">
          <span className="stat-icon">✅</span>
          <div className="stat-info">
            <span className="stat-value">{teamMembers.filter(m => m.lastCheckin).length}</span>
            <span className="stat-label">Checked In Today</span>
          </div>
        </div>
        <div className="stat-card">
          <span className="stat-icon">⏱️</span>
          <div className="stat-info">
            <span className="stat-value">{teamMembers.reduce((sum, m) => sum + m.hoursThisWeek, 0)}</span>
            <span className="stat-label">Hours This Week</span>
          </div>
        </div>
        <div className="stat-card">
          <span className="stat-icon">📁</span>
          <div className="stat-info">
            <span className="stat-value">{teamMembers.reduce((sum, m) => sum + m.projects, 0)}</span>
            <span className="stat-label">Active Assignments</span>
          </div>
        </div>
      </div>

      {/* Team List */}
      <div className="team-list">
        {filteredMembers.map(member => (
          <TeamMemberCard 
            key={member.id}
            member={member}
            onSelect={() => setSelectedMember(member)}
          />
        ))}
      </div>

      {/* Add Member Modal */}
      {showAddModal && (
        <AddMemberModal 
          onClose={() => setShowAddModal(false)}
          onAdd={(newMember) => {
            setTeamMembers(prev => [...prev, newMember]);
            setShowAddModal(false);
          }}
        />
      )}

      {/* Member Detail Modal */}
      {selectedMember && (
        <MemberDetailModal 
          member={selectedMember}
          onClose={() => setSelectedMember(null)}
          onUpdate={(updated) => {
            setTeamMembers(prev => prev.map(m => m.id === updated.id ? updated : m));
            setSelectedMember(null);
          }}
        />
      )}
    </div>
  );
};

// Team Member Card Component
const TeamMemberCard = ({ member, onSelect }) => {
  return (
    <div className={`team-card ${member.status}`} onClick={onSelect}>
      <div className="card-header">
        <div className="member-avatar">
          {member.name.split(' ').map(n => n[0]).join('')}
        </div>
        <div className="member-info">
          <h4>{member.name}</h4>
          <span className="role">{member.role}</span>
        </div>
        <div className="member-badges">
          <span className={`badge type ${member.type}`}>{member.type}</span>
          <span className={`badge status ${member.status}`}>{member.status}</span>
        </div>
      </div>
      
      <div className="card-stats">
        <div className="stat">
          <span className="label">Rate</span>
          <span className="value">
            ${member.rate}{member.rateType === 'hourly' ? '/hr' : '/yr'}
          </span>
        </div>
        <div className="stat">
          <span className="label">Projects</span>
          <span className="value">{member.projects}</span>
        </div>
        <div className="stat">
          <span className="label">This Week</span>
          <span className="value">{member.hoursThisWeek} hrs</span>
        </div>
        <div className="stat">
          <span className="label">Points</span>
          <span className="value">🎁 {member.rewardPoints}</span>
        </div>
      </div>

      <div className="card-footer">
        <span className={`checkin-status ${member.lastCheckin ? 'checked' : 'pending'}`}>
          {member.lastCheckin 
            ? `✅ Last check-in: ${member.lastCheckin.split(' ').slice(1).join(' ')}`
            : '⏳ Pending check-in'
          }
        </span>
      </div>
    </div>
  );
};

// Add Member Modal
const AddMemberModal = ({ onClose, onAdd }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    type: '1099',
    role: '',
    rate: '',
    rateType: 'hourly'
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const newMember = {
      id: `TM-${Date.now()}`,
      ...formData,
      rate: parseFloat(formData.rate),
      status: 'active',
      projects: 0,
      hoursThisWeek: 0,
      earnings: { month: 0, ytd: 0 },
      rewardPoints: 0,
      joinDate: new Date().toISOString().split('T')[0],
      lastCheckin: null
    };

    if (formData.type === 'W2') {
      newMember.taxWithholding = { federal: 22, state: 5, fica: 7.65 };
      newMember.rateType = 'salary';
    }

    onAdd(newMember);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Add Team Member</h3>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>
        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Full Name *</label>
            <input 
              type="text"
              value={formData.name}
              onChange={e => setFormData({...formData, name: e.target.value})}
              required
            />
          </div>
          
          <div className="form-group">
            <label>Email *</label>
            <input 
              type="email"
              value={formData.email}
              onChange={e => setFormData({...formData, email: e.target.value})}
              required
            />
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label>Employment Type *</label>
              <select 
                value={formData.type}
                onChange={e => setFormData({
                  ...formData, 
                  type: e.target.value,
                  rateType: e.target.value === 'W2' ? 'salary' : 'hourly'
                })}
              >
                <option value="1099">1099 Contractor</option>
                <option value="W2">W2 Employee</option>
              </select>
            </div>
            
            <div className="form-group">
              <label>Role *</label>
              <input 
                type="text"
                value={formData.role}
                onChange={e => setFormData({...formData, role: e.target.value})}
                placeholder="e.g., Senior Drafter"
                required
              />
            </div>
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label>{formData.type === 'W2' ? 'Annual Salary' : 'Hourly Rate'} *</label>
              <div className="input-with-prefix">
                <span>$</span>
                <input 
                  type="number"
                  value={formData.rate}
                  onChange={e => setFormData({...formData, rate: e.target.value})}
                  placeholder={formData.type === 'W2' ? '50000' : '45'}
                  required
                />
              </div>
            </div>
          </div>

          {formData.type === 'W2' && (
            <div className="tax-info-box">
              <h4>💼 W2 Employee Tax Withholding</h4>
              <p>Standard withholding will be applied:</p>
              <ul>
                <li>Federal: 22% (adjustable after W-4)</li>
                <li>State (PA): 3.07%</li>
                <li>FICA: 7.65%</li>
              </ul>
            </div>
          )}

          {formData.type === '1099' && (
            <div className="tax-info-box contractor">
              <h4>📋 1099 Contractor</h4>
              <p>A 1099-NEC will be issued for payments over $600/year.</p>
              <p>Contractor is responsible for their own taxes.</p>
            </div>
          )}

          <div className="modal-actions">
            <button type="button" className="btn secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn primary">
              Add Team Member
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Member Detail Modal
const MemberDetailModal = ({ member, onClose, onUpdate }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [showAssign, setShowAssign] = useState(false);
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [busy, setBusy] = useState(false);

  const handleShowAssign = async () => {
    setShowAssign(true);
    const result = await projectApi.list();
    if (result.success) {
      setProjects((result.data && result.data.projects) || []);
    } else {
      alert(`Failed to load projects: ${result.error}`);
      setShowAssign(false);
    }
  };

  const handleAssignProject = async () => {
    if (!selectedProjectId) return;
    setBusy(true);
    const result = await api.post('/api/assignments/', {
      project_id: Number(selectedProjectId),
      user_id: member.platform_user_id || member.id,
      role: 'drafter'
    });
    setBusy(false);
    if (result.success) {
      setShowAssign(false);
      setSelectedProjectId('');
      onUpdate({ ...member, projects: member.projects + 1 });
    } else {
      alert(`Failed to assign project: ${result.error}`);
    }
  };

  const handleSendMessage = () => {
    window.location.href = `mailto:${member.email}`;
  };

  const handleSendReward = async () => {
    const pointsInput = window.prompt(`Points to award to ${member.name}:`, '50');
    if (pointsInput === null) return;
    const points = parseInt(pointsInput, 10);
    if (!points || points <= 0) {
      alert('Please enter a valid number of points.');
      return;
    }
    const reason = window.prompt('Reason for award:', '');
    if (reason === null) return;
    setBusy(true);
    const result = await api.post('/api/rewards/award', {
      member_id: member.id,
      points,
      reason
    });
    setBusy(false);
    if (result.success) {
      const newBalance = result.data && typeof result.data.new_balance === 'number'
        ? result.data.new_balance
        : member.rewardPoints + points;
      onUpdate({ ...member, rewardPoints: newBalance });
    } else {
      alert(`Failed to send reward: ${result.error}`);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal large" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="member-header">
            <div className="avatar large">
              {member.name.split(' ').map(n => n[0]).join('')}
            </div>
            <div>
              <h3>{member.name}</h3>
              <span className="role">{member.role}</span>
              <div className="badges">
                <span className={`badge type ${member.type}`}>{member.type}</span>
                <span className={`badge status ${member.status}`}>{member.status}</span>
              </div>
            </div>
          </div>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>

        <div className="modal-tabs">
          <button 
            className={activeTab === 'overview' ? 'active' : ''}
            onClick={() => setActiveTab('overview')}
          >
            Overview
          </button>
          <button 
            className={activeTab === 'projects' ? 'active' : ''}
            onClick={() => setActiveTab('projects')}
          >
            Projects
          </button>
          <button 
            className={activeTab === 'earnings' ? 'active' : ''}
            onClick={() => setActiveTab('earnings')}
          >
            Earnings
          </button>
          <button 
            className={activeTab === 'rewards' ? 'active' : ''}
            onClick={() => setActiveTab('rewards')}
          >
            Rewards
          </button>
          <button 
            className={activeTab === 'documents' ? 'active' : ''}
            onClick={() => setActiveTab('documents')}
          >
            Documents
          </button>
        </div>

        <div className="modal-content">
          {activeTab === 'overview' && (
            <div className="overview-content">
              <div className="info-grid">
                <div className="info-item">
                  <label>Email</label>
                  <span>{member.email}</span>
                </div>
                <div className="info-item">
                  <label>Join Date</label>
                  <span>{member.joinDate}</span>
                </div>
                <div className="info-item">
                  <label>Rate</label>
                  <span>${member.rate}{member.rateType === 'hourly' ? '/hr' : '/yr'}</span>
                </div>
                <div className="info-item">
                  <label>Hours This Week</label>
                  <span>{member.hoursThisWeek} hrs</span>
                </div>
              </div>

              <div className="quick-actions">
                <button className="btn" onClick={handleShowAssign} disabled={busy}>📤 Assign Project</button>
                <button className="btn" onClick={handleSendMessage}>💬 Send Message</button>
                <button className="btn" onClick={handleSendReward} disabled={busy}>🎁 Send Reward</button>
                <button className="btn warning" disabled title="Coming soon">⏸️ Deactivate</button>
              </div>

              {showAssign && (
                <div className="assign-project-panel">
                  <select
                    value={selectedProjectId}
                    onChange={e => setSelectedProjectId(e.target.value)}
                  >
                    <option value="">
                      {projects.length === 0 ? 'Loading projects...' : 'Select a project...'}
                    </option>
                    {projects.map(p => (
                      <option key={p.id} value={p.id}>
                        {p.project_number} - {p.name}
                      </option>
                    ))}
                  </select>
                  <button
                    className="btn primary"
                    onClick={handleAssignProject}
                    disabled={busy || !selectedProjectId}
                  >
                    Assign
                  </button>
                  <button className="btn secondary" onClick={() => setShowAssign(false)}>
                    Cancel
                  </button>
                </div>
              )}
            </div>
          )}

          {activeTab === 'earnings' && (
            <div className="earnings-content">
              <div className="earnings-summary">
                <div className="earnings-card">
                  <span className="label">This Month</span>
                  <span className="value">${member.earnings.month.toLocaleString()}</span>
                </div>
                <div className="earnings-card">
                  <span className="label">Year to Date</span>
                  <span className="value">${member.earnings.ytd.toLocaleString()}</span>
                </div>
              </div>

              {member.type === 'W2' && member.taxWithholding && (
                <div className="tax-summary">
                  <h4>Tax Withholding</h4>
                  <div className="tax-breakdown">
                    <div className="tax-item">
                      <span>Federal ({member.taxWithholding.federal}%)</span>
                      <span>${(member.earnings.month * member.taxWithholding.federal / 100).toFixed(2)}</span>
                    </div>
                    <div className="tax-item">
                      <span>State ({member.taxWithholding.state}%)</span>
                      <span>${(member.earnings.month * member.taxWithholding.state / 100).toFixed(2)}</span>
                    </div>
                    <div className="tax-item">
                      <span>FICA ({member.taxWithholding.fica}%)</span>
                      <span>${(member.earnings.month * member.taxWithholding.fica / 100).toFixed(2)}</span>
                    </div>
                    <div className="tax-item total">
                      <span>Net Pay</span>
                      <span>${(member.earnings.month * (1 - (member.taxWithholding.federal + member.taxWithholding.state + member.taxWithholding.fica) / 100)).toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              )}

              <button className="btn primary" disabled title="Coming soon">
                📊 View Full Payment History
              </button>
            </div>
          )}

          {activeTab === 'rewards' && (
            <div className="rewards-content">
              <div className="points-balance">
                <span className="points-icon">🎁</span>
                <span className="points-value">{member.rewardPoints}</span>
                <span className="points-label">Points Available</span>
              </div>
              
              <button className="btn primary" onClick={handleSendReward} disabled={busy}>
                🎁 Send Bonus Points
              </button>
              
              <div className="rewards-history">
                <h4>Recent Rewards</h4>
                <div className="history-item">
                  <span>🎉 +50 pts - Completed project on time</span>
                  <span className="date">Jan 5</span>
                </div>
                <div className="history-item">
                  <span>☕ -100 pts - $10 Wawa Gift Card</span>
                  <span className="date">Jan 3</span>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'documents' && (
            <div className="documents-content">
              <h4>Required Documents</h4>
              {member.type === '1099' ? (
                <div className="doc-list">
                  <div className="doc-item">
                    <span>📄 W-9 Form</span>
                    <span className="status complete">✓ On File</span>
                  </div>
                  <div className="doc-item">
                    <span>📄 Independent Contractor Agreement</span>
                    <span className="status complete">✓ Signed</span>
                  </div>
                  <div className="doc-item">
                    <span>📄 2023 1099-NEC</span>
                    <span className="status complete">✓ Issued</span>
                  </div>
                </div>
              ) : (
                <div className="doc-list">
                  <div className="doc-item">
                    <span>📄 W-4 Form</span>
                    <span className="status complete">✓ On File</span>
                  </div>
                  <div className="doc-item">
                    <span>📄 I-9 Form</span>
                    <span className="status complete">✓ Verified</span>
                  </div>
                  <div className="doc-item">
                    <span>📄 Employment Agreement</span>
                    <span className="status complete">✓ Signed</span>
                  </div>
                  <div className="doc-item">
                    <span>📄 2023 W-2</span>
                    <span className="status complete">✓ Issued</span>
                  </div>
                </div>
              )}
              
              <button className="btn" disabled title="Coming soon">
                📤 Upload Document
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TeamManagement;
